/**
 * NegotiationCalculator Component (Phase 50.1-05)
 *
 * Interactive "what-if" calculator for exploring different seller financing
 * negotiation scenarios in real-time without API calls.
 *
 * Features:
 * - Interest rate slider (4% - 10%, 0.25% steps)
 * - Down payment slider (5% - 30%, 5% steps)
 * - Real-time metric cards: Monthly Payment, Cash Required, Cash-on-Cash
 * - Break-even indicator (green if below, red if above)
 * - Delta hints showing comparison to bank financing
 * - Preset negotiation buttons: "Match Bank Payment", "10% Better CoC", "Minimum Down"
 *
 * Uses luxury dark theme (charcoal bg, gold accents, JetBrains Mono for numbers).
 */

import React, { useState, useMemo } from 'react';
import {
  Calculator,
  TrendingUp,
  TrendingDown,
  Target,
  DollarSign,
  Percent,
  ChevronRight,
  Zap,
} from 'lucide-react';
import { useFinancingCalculator } from '../../hooks/useFinancingCalculator';
import { formatCurrency, formatPercent } from '../../utils/formatters';

// =============================================================================
// Types
// =============================================================================

export interface NegotiationCalculatorProps {
  purchasePrice: number;
  noi: number;
  bankRate?: number;
  bankDownPct?: number;
  className?: string;
}

// =============================================================================
// Helper Components
// =============================================================================

/** Metric card with value, delta, and win/lose indicator */
const MetricCard: React.FC<{
  label: string;
  value: string;
  delta: number;
  deltaLabel: string;
  invertDelta?: boolean; // True if positive delta is good (e.g., CoC)
  icon: React.ReactNode;
}> = ({ label, value, delta, deltaLabel, invertDelta = false, icon }) => {
  const isWin = invertDelta ? delta > 0 : delta < 0;
  const isNeutral = Math.abs(delta) < 0.001;

  return (
    <div className="bg-obsidian/50 rounded-lg p-4 border border-slate-dark/50">
      <div className="flex items-center gap-2 text-sm text-stone-700 uppercase tracking-wider mb-2">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-mono tabular-nums font-bold text-ivory">{value}</div>
      {!isNeutral && (
        <div
          className={`flex items-center gap-1 text-sm mt-1 ${
            isWin ? 'text-emerald' : 'text-crimson'
          }`}
        >
          {isWin ? (
            <TrendingUp className="w-3 h-3" />
          ) : (
            <TrendingDown className="w-3 h-3" />
          )}
          <span className="font-mono tabular-nums">{deltaLabel}</span>
        </div>
      )}
    </div>
  );
};

/** Preset button for quick scenario selection */
const PresetButton: React.FC<{
  label: string;
  description: string;
  onClick: () => void;
  isActive?: boolean;
}> = ({ label, description, onClick, isActive = false }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center justify-between p-3 rounded-lg border transition-all duration-200
      ${
        isActive
          ? 'bg-gold/20 border-gold text-gold'
          : 'bg-graphite/50 border-slate-dark hover:border-gold/50 hover:bg-graphite text-ivory'
      }
    `}
  >
    <div className="text-left">
      <div className="text-sm font-semibold">{label}</div>
      <div className="text-sm text-stone-700">{description}</div>
    </div>
    <ChevronRight className={`w-4 h-4 ${isActive ? 'text-gold' : 'text-stone-700'}`} />
  </button>
);

// =============================================================================
// Main Component
// =============================================================================

export const NegotiationCalculator: React.FC<NegotiationCalculatorProps> = ({
  purchasePrice,
  noi,
  bankRate = 0.07,
  bankDownPct = 0.25,
  className = '',
}) => {
  // Slider state
  const [rate, setRate] = useState(0.06); // Default 6%
  const [downPct, setDownPct] = useState(0.10); // Default 10%

  // Calculator hook
  const { bankScenario, calculateSellerScenario, breakEvenRate, calculateRateFor10PctBetterCoC } =
    useFinancingCalculator({
      purchasePrice,
      noi,
      bankRate,
      bankDownPct,
    });

  // Calculate current seller scenario
  const sellerScenario = useMemo(
    () => calculateSellerScenario(downPct, rate),
    [calculateSellerScenario, downPct, rate]
  );

  // Determine if current rate is below break-even (seller wins)
  const isBelowBreakEven = rate < breakEvenRate;

  // ==========================================================================
  // Preset Button Handlers
  // ==========================================================================

  const handleMatchBankPayment = () => {
    setRate(Math.min(0.10, Math.max(0.04, breakEvenRate)));
    setDownPct(0.10);
  };

  const handle10PctBetterCoC = () => {
    const targetRate = calculateRateFor10PctBetterCoC();
    setRate(targetRate);
    setDownPct(0.10);
  };

  const handleMinimumDown = () => {
    setDownPct(0.05);
    // Keep current rate or set to a competitive rate
    if (rate > 0.07) setRate(0.06);
  };

  // Check which preset is active
  const isMatchBankActive = Math.abs(rate - breakEvenRate) < 0.005 && Math.abs(downPct - 0.10) < 0.01;
  const is10PctBetterActive =
    Math.abs(rate - calculateRateFor10PctBetterCoC()) < 0.005 && Math.abs(downPct - 0.10) < 0.01;
  const isMinDownActive = Math.abs(downPct - 0.05) < 0.01;

  // ==========================================================================
  // Slider Handlers
  // ==========================================================================

  const handleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRate(parseFloat(e.target.value) / 100);
  };

  const handleDownPctChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDownPct(parseInt(e.target.value) / 100);
  };

  // ==========================================================================
  // Format Deltas for Display
  // ==========================================================================

  const formatPaymentDelta = () => {
    const delta = sellerScenario.paymentDelta;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${formatCurrency(delta)}/mo vs bank`;
  };

  const formatEquityDelta = () => {
    const delta = sellerScenario.equityDelta;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${formatCurrency(delta)} vs bank`;
  };

  const formatCoCDelta = () => {
    const delta = sellerScenario.cocDelta;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${(delta * 100).toFixed(1)}% vs bank`;
  };

  return (
    <div className={`bg-charcoal rounded-lg border border-slate-dark ${className}`}>
      {/* Header */}
      <div className="p-5 border-b border-slate-dark">
        <div className="flex items-center gap-3">
          <Calculator className="w-5 h-5 text-gold" />
          <h3 className="text-lg font-serif text-ivory">Negotiation Calculator</h3>
        </div>
        <p className="text-sm text-stone-700 mt-1">
          Explore different financing scenarios in real-time
        </p>
      </div>

      {/* Preset Buttons */}
      <div className="p-5 border-b border-slate-dark">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-gold" />
          <span className="text-sm uppercase tracking-wider text-stone-700 font-semibold">
            Quick Scenarios
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <PresetButton
            label="Match Bank Payment"
            description={`${(breakEvenRate * 100).toFixed(1)}% rate`}
            onClick={handleMatchBankPayment}
            isActive={isMatchBankActive}
          />
          <PresetButton
            label="10% Better CoC"
            description={`${(calculateRateFor10PctBetterCoC() * 100).toFixed(1)}% rate`}
            onClick={handle10PctBetterCoC}
            isActive={is10PctBetterActive}
          />
          <PresetButton
            label="Minimum Down (5%)"
            description={formatCurrency(purchasePrice * 0.05)}
            onClick={handleMinimumDown}
            isActive={isMinDownActive}
          />
        </div>
      </div>

      {/* Sliders */}
      <div className="p-5 border-b border-slate-dark space-y-6">
        {/* Interest Rate Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm uppercase tracking-wider text-stone-700 font-semibold flex items-center gap-2">
              <Percent className="w-3 h-3" />
              Interest Rate
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gold font-mono tabular-nums text-xl font-bold">
                {(rate * 100).toFixed(2)}%
              </span>
              {/* Break-even indicator */}
              <span
                className={`text-sm px-2 py-0.5 rounded-full font-semibold ${
                  isBelowBreakEven
                    ? 'bg-emerald/20 text-emerald'
                    : 'bg-crimson/20 text-crimson'
                }`}
              >
                {isBelowBreakEven ? 'Below Break-Even' : 'Above Break-Even'}
              </span>
            </div>
          </div>
          <div className="relative">
            <input
              type="range"
              min={4}
              max={10}
              step={0.25}
              value={rate * 100}
              onChange={handleRateChange}
              className="w-full h-2 bg-graphite rounded-lg appearance-none cursor-pointer
                       [&::-webkit-slider-thumb]:appearance-none
                       [&::-webkit-slider-thumb]:w-5
                       [&::-webkit-slider-thumb]:h-5
                       [&::-webkit-slider-thumb]:rounded-full
                       [&::-webkit-slider-thumb]:bg-gold
                       [&::-webkit-slider-thumb]:cursor-pointer
                       [&::-webkit-slider-thumb]:shadow-lg
                       [&::-webkit-slider-thumb]:shadow-gold/30
                       [&::-webkit-slider-thumb]:hover:bg-gold-bright
                       [&::-webkit-slider-thumb]:transition-colors"
            />
            {/* Break-even marker */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-gold/60"
              style={{
                left: `${((breakEvenRate * 100 - 4) / 6) * 100}%`,
              }}
              title={`Break-even: ${(breakEvenRate * 100).toFixed(1)}%`}
            />
          </div>
          <div className="flex justify-between text-sm text-stone-700">
            <span>4% (aggressive)</span>
            <span className="text-gold/60">
              <Target className="w-3 h-3 inline mr-1" />
              {(breakEvenRate * 100).toFixed(1)}% break-even
            </span>
            <span>10% (premium)</span>
          </div>
        </div>

        {/* Down Payment Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm uppercase tracking-wider text-stone-700 font-semibold flex items-center gap-2">
              <DollarSign className="w-3 h-3" />
              Down Payment
            </label>
            <span className="text-gold font-mono tabular-nums text-xl font-bold">
              {(downPct * 100).toFixed(0)}%
            </span>
          </div>
          <input
            type="range"
            min={5}
            max={30}
            step={5}
            value={downPct * 100}
            onChange={handleDownPctChange}
            className="w-full h-2 bg-graphite rounded-lg appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-5
                     [&::-webkit-slider-thumb]:h-5
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-gold
                     [&::-webkit-slider-thumb]:cursor-pointer
                     [&::-webkit-slider-thumb]:shadow-lg
                     [&::-webkit-slider-thumb]:shadow-gold/30
                     [&::-webkit-slider-thumb]:hover:bg-gold-bright
                     [&::-webkit-slider-thumb]:transition-colors"
          />
          <div className="flex justify-between text-sm text-stone-700">
            <span>5% ({formatCurrency(purchasePrice * 0.05)})</span>
            <span>30% ({formatCurrency(purchasePrice * 0.30)})</span>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="p-5 border-b border-slate-dark">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            label="Monthly Payment"
            value={formatCurrency(sellerScenario.payment)}
            delta={sellerScenario.paymentDelta}
            deltaLabel={formatPaymentDelta()}
            invertDelta={false}
            icon={<DollarSign className="w-3 h-3" />}
          />
          <MetricCard
            label="Cash Required"
            value={formatCurrency(sellerScenario.equity)}
            delta={sellerScenario.equityDelta}
            deltaLabel={formatEquityDelta()}
            invertDelta={false}
            icon={<DollarSign className="w-3 h-3" />}
          />
          <MetricCard
            label="Cash-on-Cash"
            value={formatPercent(sellerScenario.coc, 1)}
            delta={sellerScenario.cocDelta}
            deltaLabel={formatCoCDelta()}
            invertDelta={true}
            icon={<Percent className="w-3 h-3" />}
          />
        </div>
      </div>

      {/* Comparison Summary */}
      <div className="p-5">
        <div
          className={`p-4 rounded-lg border ${
            isBelowBreakEven
              ? 'bg-emerald/10 border-emerald/30'
              : 'bg-crimson/10 border-crimson/30'
          }`}
        >
          <div className="flex items-start gap-3">
            {isBelowBreakEven ? (
              <TrendingUp className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
            ) : (
              <TrendingDown className="w-5 h-5 text-crimson flex-shrink-0 mt-0.5" />
            )}
            <div>
              <span
                className={`text-sm font-semibold uppercase tracking-wider ${
                  isBelowBreakEven ? 'text-emerald' : 'text-crimson'
                }`}
              >
                {isBelowBreakEven ? 'Seller Financing Wins' : 'Bank Financing Better'}
              </span>
              <p className="text-sm text-stone-700 mt-1">
                {isBelowBreakEven ? (
                  <>
                    At{' '}
                    <span className="font-mono tabular-nums font-semibold text-emerald">
                      {(rate * 100).toFixed(2)}%
                    </span>{' '}
                    with{' '}
                    <span className="font-mono tabular-nums font-semibold text-emerald">
                      {(downPct * 100).toFixed(0)}%
                    </span>{' '}
                    down, you save{' '}
                    <span className="font-mono tabular-nums font-semibold text-emerald">
                      {formatCurrency(Math.abs(sellerScenario.paymentDelta))}
                    </span>
                    /mo and need{' '}
                    <span className="font-mono tabular-nums font-semibold text-emerald">
                      {formatCurrency(Math.abs(sellerScenario.equityDelta))}
                    </span>{' '}
                    less cash.
                  </>
                ) : (
                  <>
                    At{' '}
                    <span className="font-mono tabular-nums font-semibold text-crimson">
                      {(rate * 100).toFixed(2)}%
                    </span>
                    , you're paying{' '}
                    <span className="font-mono tabular-nums font-semibold text-crimson">
                      {formatCurrency(sellerScenario.paymentDelta)}
                    </span>
                    /mo more than bank financing. Negotiate below{' '}
                    <span className="font-mono tabular-nums font-semibold text-gold">
                      {(breakEvenRate * 100).toFixed(1)}%
                    </span>{' '}
                    to win.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Bank Comparison Reference */}
        <div className="mt-4 p-3 bg-obsidian/30 rounded border border-slate-dark/30">
          <div className="text-sm text-stone-700 uppercase tracking-wider mb-2">
            Bank Baseline Reference
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-stone-700">Payment:</span>{' '}
              <span className="font-mono tabular-nums text-ivory">{formatCurrency(bankScenario.payment)}/mo</span>
            </div>
            <div>
              <span className="text-stone-700">Down:</span>{' '}
              <span className="font-mono tabular-nums text-ivory">{formatCurrency(bankScenario.equity)}</span>
            </div>
            <div>
              <span className="text-stone-700">CoC:</span>{' '}
              <span className="font-mono tabular-nums text-ivory">{formatPercent(bankScenario.coc, 1)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NegotiationCalculator;
